import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Plus, Edit2, Trash2, Save, X, AlertCircle, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const INP = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#1F7A6E] focus:border-[#1F7A6E]";

const KIND_LABELS = {
  percent: '% of basis',
  flat: 'Flat amount',
  conditional_flat: 'Conditional flat',
  balancer: 'Balancer (auto)',
};

export default function SalaryStructureMaster() {
  const [components, setComponents] = useState([]);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // component being edited (object) or {} for new
  const [previewGross, setPreviewGross] = useState(25000);
  const [previewOptOut, setPreviewOptOut] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, rRes] = await Promise.all([
        fetch(`${API}/api/hr/salary-master/components`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/hr/salary-master/pf-rules`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setComponents(cRes);
      setRules(rRes);
    } catch (e) {
      toast.error('Failed to load Salary Master');
    } finally {
      setLoading(false);
    }
  };

  const doPreview = async () => {
    try {
      const r = await fetch(`${API}/api/hr/salary-master/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gross: parseFloat(previewGross) || 0, opt_out_pf: previewOptOut }),
      });
      setPreview(await r.json());
    } catch {
      toast.error('Preview failed');
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (components.length) doPreview(); }, [previewGross, previewOptOut, components]);

  const saveComponent = async (c) => {
    const method = c.id ? 'PUT' : 'POST';
    const url = `${API}/api/hr/salary-master/components${c.id ? '/' + c.id : ''}`;
    try {
      const r = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(c),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Save failed');
        return;
      }
      toast.success(c.id ? 'Component updated' : 'Component added');
      setEditing(null);
      fetchAll();
    } catch {
      toast.error('Network error');
    }
  };

  const deleteComponent = async (id) => {
    if (!window.confirm('Delete this component? Existing employee salary breakdowns will not be affected until they are recalculated.')) return;
    const r = await fetch(`${API}/api/hr/salary-master/components/${id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) {
      toast.success('Deleted');
      fetchAll();
    }
  };

  const saveRules = async () => {
    const r = await fetch(`${API}/api/hr/salary-master/pf-rules`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rules),
    });
    if (r.ok) {
      toast.success('PF/ESI rules saved');
    } else {
      toast.error('Save failed');
    }
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="salary-master-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Masters · HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Salary Structure Master</h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Define how monthly gross salary breaks into components. Changes apply to new employees and any
            employee whose record is re-saved. PF & ESI rules below follow Indian statutory limits.
          </p>
        </div>
      </div>

      {/* Components Table */}
      <div className="border border-slate-200 rounded-sm bg-white">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-base">Components</h2>
          <button
            onClick={() => setEditing({ code: '', name: '', kind: 'percent', basis: 'gross', value: 0, order: components.length + 1, active: true })}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-semibold"
            data-testid="add-component-btn"
          >
            <Plus size={14} /> Add Component
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase font-bold tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Order</th>
              <th className="px-4 py-2 text-left">Code</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Kind</th>
              <th className="px-4 py-2 text-left">Basis / Value</th>
              <th className="px-4 py-2 text-left">Conditional</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {components.map(c => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 text-slate-500">{c.order}</td>
                <td className="px-4 py-2.5 font-mono font-bold text-[#1F7A6E]">{c.code}</td>
                <td className="px-4 py-2.5">{c.name}</td>
                <td className="px-4 py-2.5 text-xs">{KIND_LABELS[c.kind]}</td>
                <td className="px-4 py-2.5 text-xs">
                  {c.kind === 'percent' && <>{c.value}% of {c.basis}</>}
                  {c.kind === 'flat' && <>₹{c.value.toLocaleString()}</>}
                  {c.kind === 'conditional_flat' && <>₹{c.value.toLocaleString()}</>}
                  {c.kind === 'balancer' && <span className="text-slate-400">auto</span>}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500">
                  {c.conditional && c.kind === 'conditional_flat' && (
                    <>if gross &gt; ₹{c.conditional.threshold.toLocaleString()}, else ₹{c.conditional.else_value.toLocaleString()}</>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-slate-200 rounded-sm" data-testid={`edit-${c.code}`}>
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => deleteComponent(c.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded-sm">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PF / ESI rules */}
      <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-bold text-base">PF &amp; ESI Rules</h2>
          <button onClick={saveRules} className="px-3 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-xs font-semibold flex items-center gap-1.5" data-testid="save-pf-rules">
            <Save size={13} /> Save Rules
          </button>
        </div>
        {rules && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="PF mandatory above gross (₹)">
              <input type="number" value={rules.pf_mandatory_min_gross} onChange={e => setRules({ ...rules, pf_mandatory_min_gross: +e.target.value })} className={INP} />
            </Field>
            <Field label="Employer PF only below gross (₹)">
              <input type="number" value={rules.employer_pf_max_gross} onChange={e => setRules({ ...rules, employer_pf_max_gross: +e.target.value })} className={INP} />
            </Field>
            <Field label="Employee PF %">
              <input type="number" step="0.01" value={rules.employee_pf_pct} onChange={e => setRules({ ...rules, employee_pf_pct: +e.target.value })} className={INP} />
            </Field>
            <Field label="Employer PF %">
              <input type="number" step="0.01" value={rules.employer_pf_pct} onChange={e => setRules({ ...rules, employer_pf_pct: +e.target.value })} className={INP} />
            </Field>
            <Field label="PF wage ceiling (₹)">
              <input type="number" value={rules.pf_wage_ceiling} onChange={e => setRules({ ...rules, pf_wage_ceiling: +e.target.value })} className={INP} />
            </Field>
            <Field label="ESI max gross (₹)">
              <input type="number" value={rules.esi_max_gross} onChange={e => setRules({ ...rules, esi_max_gross: +e.target.value })} className={INP} />
            </Field>
            <Field label="Employee ESI %">
              <input type="number" step="0.01" value={rules.employee_esi_pct} onChange={e => setRules({ ...rules, employee_esi_pct: +e.target.value })} className={INP} />
            </Field>
            <Field label="Employer ESI %">
              <input type="number" step="0.01" value={rules.employer_esi_pct} onChange={e => setRules({ ...rules, employer_esi_pct: +e.target.value })} className={INP} />
            </Field>
          </div>
        )}
      </div>

      {/* Live preview */}
      <div className="border border-teal-200 bg-teal-50/40 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-[#1F7A6E]" />
          <h2 className="font-display font-bold text-base">Live Preview</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-3">
            <Field label="Test gross salary (₹)">
              <input type="number" value={previewGross} onChange={e => setPreviewGross(e.target.value)} className={INP} data-testid="preview-gross" />
            </Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={previewOptOut} onChange={e => setPreviewOptOut(e.target.checked)} className="rounded" />
              Employee opted out of PF (Form 11)
            </label>
          </div>
          <div className="md:col-span-2">
            {preview && (
              <div className="bg-white border border-slate-200 rounded-sm p-3 text-sm">
                <div className="space-y-1.5">
                  {preview.components.map(c => (
                    <div key={c.code} className="flex justify-between">
                      <span className="text-slate-600">{c.name} ({c.code})</span>
                      <span className="font-mono">₹{c.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5 font-bold">
                    <span>Gross</span>
                    <span className="font-mono">₹{preview.gross.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-red-600 text-xs">
                    <span>Less: Employee PF {preview.deductions.opt_out_pf ? '(opted out)' : preview.deductions.pf_applies ? '' : '(not applicable)'}</span>
                    <span className="font-mono">₹{preview.deductions.employee_pf.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-red-600 text-xs">
                    <span>Less: Employee ESI {preview.deductions.esi_applies ? '' : '(not applicable)'}</span>
                    <span className="font-mono">₹{preview.deductions.employee_esi.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5 font-bold text-emerald-700">
                    <span>Net take-home</span>
                    <span className="font-mono">₹{preview.net.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 pt-2">
                    Employer PF: ₹{preview.deductions.employer_pf.toLocaleString('en-IN')} · Employer ESI: ₹{preview.deductions.employer_esi.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && <ComponentModal initial={editing} onClose={() => setEditing(null)} onSave={saveComponent} />}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ComponentModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    ...initial,
    conditional: initial.conditional || { threshold: 0, else_value: 0 },
  });
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md max-w-lg w-full shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold">{initial.id ? 'Edit' : 'Add'} Component</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code *">
              <input value={form.code} onChange={e => u('code', e.target.value.toUpperCase())} className={INP} placeholder="BASIC, DA, ..." data-testid="comp-code" />
            </Field>
            <Field label="Order">
              <input type="number" value={form.order} onChange={e => u('order', +e.target.value)} className={INP} />
            </Field>
          </div>
          <Field label="Name *">
            <input value={form.name} onChange={e => u('name', e.target.value)} className={INP} placeholder="Basic Pay, House Rent Allowance, ..." data-testid="comp-name" />
          </Field>
          <Field label="Kind *">
            <select value={form.kind} onChange={e => u('kind', e.target.value)} className={INP} data-testid="comp-kind">
              <option value="percent">% of basis</option>
              <option value="flat">Flat amount</option>
              <option value="conditional_flat">Conditional flat amount</option>
              <option value="balancer">Balancer (auto-fills the gap)</option>
            </select>
          </Field>
          {form.kind === 'percent' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="% of *">
                <select value={form.basis || 'gross'} onChange={e => u('basis', e.target.value)} className={INP}>
                  <option value="gross">Gross</option>
                  <option value="basic">Basic</option>
                </select>
              </Field>
              <Field label="Percent *">
                <input type="number" step="0.01" value={form.value} onChange={e => u('value', +e.target.value)} className={INP} placeholder="50" />
              </Field>
            </div>
          )}
          {(form.kind === 'flat' || form.kind === 'conditional_flat') && (
            <Field label="Amount (₹) *">
              <input type="number" step="0.01" value={form.value} onChange={e => u('value', +e.target.value)} className={INP} />
            </Field>
          )}
          {form.kind === 'conditional_flat' && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 rounded-sm border border-amber-200">
              <Field label="Apply ONLY if gross >">
                <input type="number" value={form.conditional.threshold} onChange={e => u('conditional', { ...form.conditional, threshold: +e.target.value })} className={INP} />
              </Field>
              <Field label="Else use amount (₹)">
                <input type="number" value={form.conditional.else_value} onChange={e => u('conditional', { ...form.conditional, else_value: +e.target.value })} className={INP} />
              </Field>
            </div>
          )}
          {form.kind === 'balancer' && (
            <div className="flex gap-2 items-start p-3 bg-teal-50 rounded-sm border border-teal-200 text-xs text-slate-700">
              <AlertCircle size={14} className="mt-0.5 text-[#1F7A6E]" />
              <span>Balancer fills the gap so component total = gross. Only ONE active balancer is allowed.</span>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm pt-2">
            <input type="checkbox" checked={form.active} onChange={e => u('active', e.target.checked)} className="rounded" />
            Active
          </label>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-sm text-sm">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-semibold flex items-center gap-1.5" data-testid="save-component">
            <Save size={14} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
